import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Target, Tag, ChevronDown, ChevronUp, Info, AlertCircle } from 'lucide-react';
import api from '../utils/api';


const ScopeTemplatesSettings = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]); // Fetched from portal settings
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', request_type_mappings: [] });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedTemplates, setExpandedTemplates] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [templatesResponse, requestTypesResponse] = await Promise.all([
        api.get(`/settings/scope-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get(`/settings/portal-request-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTemplates(templatesResponse.data);
      setRequestTypes(requestTypesResponse.data?.request_types || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load scope templates' });
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        name: formData.name,
        description: formData.description || ''
      };

      let templateId = editingTemplate?.id;

      if (editingTemplate) {
        await api.put(`/settings/scope-templates/${editingTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Scope template updated successfully' });
      } else {
        const response = await api.post(`/settings/scope-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        templateId = response.data?.template?.id;
        setMessage({ type: 'success', text: 'Scope template created successfully' });
      }

      // Update request type mappings if we have them and a template ID
      if (templateId && formData.request_type_mappings && formData.request_type_mappings.length > 0) {
        await api.put(`/settings/scope-templates/${templateId}/request-types`, {
          request_types: formData.request_type_mappings
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchData();
      resetForm();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving scope template:', error);
      setMessage({ type: 'error', text: 'Failed to save scope template' });
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this scope template?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/settings/scope-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Scope template deleted successfully' });
      fetchData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting scope template:', error);
      setMessage({ type: 'error', text: 'Failed to delete scope template' });
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ 
      name: template.name, 
      description: template.description,
      request_type_mappings: template.request_type_mappings || []
    });
    setShowAddModal(true);
  };

  const handleRequestTypeMappingChange = async (templateId, requestType, isChecked) => {
    try {
      const token = localStorage.getItem('access_token');
      const template = templates.find(t => t.id === templateId);
      if (!template) return;

      const currentMappings = template.request_type_mappings || [];
      let newMappings;
      
      if (isChecked) {
        newMappings = [...currentMappings, requestType];
      } else {
        newMappings = currentMappings.filter(t => t !== requestType);
      }

      await api.put(`/settings/scope-templates/${templateId}/request-types`, {
        request_types: newMappings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setTemplates(templates.map(t => 
        t.id === templateId 
          ? { ...t, request_type_mappings: newMappings }
          : t
      ));

      setMessage({ type: 'success', text: 'Request type mapping updated' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Error updating request type mapping:', error);
      setMessage({ type: 'error', text: 'Failed to update mapping' });
    }
  };

  const toggleTemplateExpanded = (templateId) => {
    setExpandedTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', request_type_mappings: [] });
    setEditingTemplate(null);
    setShowAddModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Project Scope Templates</h3>
          <p className="text-sm text-slate-600 mt-1">
            Create reusable scope templates to quickly assign to clients. Map default request types for workflow automation.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Scope Template
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Workflow Integration:</strong> Map request types to scope templates. When a template is assigned to a client, 
          these mappings enable workflow conditions to check if portal submissions are within the client&apos;s contracted scope.
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`rounded-lg p-4 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white shadow rounded-lg divide-y divide-slate-200">
        {templates.length === 0 ? (
          <div className="text-center py-12">
            <Target className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No scope templates</h3>
            <p className="mt-1 text-sm text-slate-500">Get started by creating a new scope template.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Scope Template
              </button>
            </div>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="hover:bg-slate-50 transition-colors">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <Target className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base font-medium text-slate-900">{template.name}</h4>
                        {template.description && (
                          <p className="mt-1 text-sm text-slate-600">{template.description}</p>
                        )}
                        
                        {/* Request Type Mappings Summary */}
                        {template.request_type_mappings && template.request_type_mappings.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {template.request_type_mappings.map((type, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <Tag className="w-3 h-3 mr-1" />
                                {type}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleTemplateExpanded(template.id)}
                      className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Manage Request Types"
                    >
                      {expandedTemplates[template.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Expanded Request Type Mapping Section */}
              {expandedTemplates[template.id] && (
                <div className="px-6 pb-6 pt-2 bg-slate-50 border-t border-slate-100">
                  <div className="ml-9">
                    <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center">
                      <Tag className="w-4 h-4 mr-2 text-indigo-600" />
                      Default Request Types for This Scope
                    </h5>
                    <p className="text-xs text-slate-500 mb-3">
                      Select which request types this scope template covers by default. When assigned to a client, these mappings enable workflow conditions.
                    </p>
                    {requestTypes.length === 0 ? (
                      <div className="flex items-center p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                        <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>No request types configured. Configure them in <strong>Client Portal Settings</strong> first.</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {requestTypes.map((type, idx) => {
                          const isChecked = (template.request_type_mappings || []).includes(type);
                          return (
                            <label 
                              key={idx} 
                              className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                                isChecked 
                                  ? 'bg-green-50 border-green-300 text-green-800' 
                                  : 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => handleRequestTypeMappingChange(template.id, type, e.target.checked)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                              />
                              <span className="ml-2 text-sm">{type}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingTemplate ? 'Edit Scope Template' : 'Add Scope Template'}
                </h3>
                <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Scope Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Website Design, Logo Design, Site Maintenance"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this scope includes..."
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Request Type Mappings in Modal */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Tag className="w-4 h-4 inline mr-1" />
                    Default Request Types Covered
                  </label>
                  <p className="text-xs text-slate-500 mb-3">
                    Select which portal request types this scope covers for workflow automation.
                  </p>
                  {requestTypes.length === 0 ? (
                    <div className="flex items-center p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>No request types configured. Configure them in <strong>Client Portal Settings</strong> first.</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3">
                      {requestTypes.map((type, idx) => {
                        const isChecked = (formData.request_type_mappings || []).includes(type);
                        return (
                          <label 
                            key={idx} 
                            className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                              isChecked 
                                ? 'bg-green-50 border-green-300 text-green-800' 
                                : 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const currentMappings = formData.request_type_mappings || [];
                                if (e.target.checked) {
                                  setFormData({ ...formData, request_type_mappings: [...currentMappings, type] });
                                } else {
                                  setFormData({ ...formData, request_type_mappings: currentMappings.filter(t => t !== type) });
                                }
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <span className="ml-2 text-sm">{type}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingTemplate ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeTemplatesSettings;
